import axios from "axios";
import { config } from "../config";
import { clearUser, setUser } from "../slices/veloxity/auth/RefleshToken";

axios.defaults.baseURL = config.api;

axios.defaults.headers.post["Content-Type"] = "application/json";

let quotaAlertCallback = null;

export const setQuotaAlertCallback = (callback) => {
    quotaAlertCallback = callback;
};

const refreshAuthToken = async () => {
  const user = JSON.parse(sessionStorage.getItem("authUser"));
  const refreshTokenValue = user.data.refreshToken;
  const deviceId = getClientId();

  try {
    const response = await axios.put("/api/v1/authentication/refresh-token", { token: refreshTokenValue }, {
      headers: {
        'Device-Id': deviceId
      }
    });
    const newTokenData = response.data;

    const updatedUser = { ...user, data: { ...user.data, accessToken: newTokenData.accessToken, refreshToken: newTokenData.refreshToken } };
    sessionStorage.setItem("authUser", JSON.stringify(updatedUser));
    
    return newTokenData.accessToken;
  } catch (error) {
    clearUser();
    window.location.href = "/login"; 
    return null;
  }
};

axios.interceptors.response.use(
  function (response) {
    return response.data ? response.data : response;
  },
  async function (error) {
    const originalRequest = error.config;

    if (error.response && error.response.status === 402) {
      if (quotaAlertCallback) {
        quotaAlertCallback(true);
      }
      return Promise.reject(error.response ? error.response.data : error.message);
    }

    if (error.response && error.response.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;
      const newAccessToken = await refreshAuthToken();
      if (newAccessToken) {
        axios.defaults.headers.common["Authorization"] = `Bearer ${newAccessToken}`;
        originalRequest.headers["Authorization"] = `Bearer ${newAccessToken}`;
        return axios(originalRequest);
      }
    }

    return Promise.reject(error.response ? error.response.data : error.message);
  }
);

const setAuthorization = (token) => {
  axios.defaults.headers.common["Authorization"] = "Bearer " + token;
};

const getClientId = () => {
  let clientId = localStorage.getItem('client_id');
  if (!clientId) {
    clientId = generateClientId();
    localStorage.setItem('client_id', clientId);
  }
  return clientId;
};

const generateClientId = () => {
  return (
    Math.random().toString(36).substring(2, 16) +
    Math.random().toString(36).substring(2, 16)
  );
};

class APIClient {
  get = (url, params) => {
    const accessToken = JSON.parse(sessionStorage.getItem("authUser")).data.accessToken;
    const deviceId = getClientId();
    const headers = {
      Authorization: `Bearer ${accessToken}`,
      'Device-Id': deviceId,
    };

    const config = { headers };
    if (params) {
      config.params = params;
    }

    return axios.get(url, config);
  };

  getBlob = (url, { params = null, responseType = 'json' } = {}) => {
    const accessToken = JSON.parse(sessionStorage.getItem("authUser")).data.accessToken;
    const deviceId = getClientId();
    const headers = {
      Authorization: `Bearer ${accessToken}`,
      'Device-Id': deviceId,
    };

    const config = {
      headers,
      responseType,
    };

    if (params) {
      config.params = params;
    }

    return axios.get(url, config);
  };

  postChatGPT = async (data) => {
    const apiKey = "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"; 
    try {
      const response = await axios.post(
        "https://api.openai.com/v1/chat/completions",
        data,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${apiKey}`,
          },
        }
      );
      return response;
    } catch (error) {
      if (error.response) {
        console.error("ChatGPT API error response:", error.response.data);
      }
      throw error.response ? error.response.data : "Network error";
    }
  };

  create = (url, data, headers = {}) => {
    const deviceId = getClientId();
    return axios.post(url, data, {
      headers: {
        'Content-Type': 'application/json',
        'Device-Id': deviceId,
        ...headers,
      },
    });
  };

  update = (url, data) => {
    const accessToken = JSON.parse(sessionStorage.getItem("authUser")).data.accessToken;
    const deviceId = getClientId();
    return axios.patch(url, data, {
      headers: {
        Authorization: `Bearer ${accessToken}`,
        'Device-Id': deviceId,
      },
    });
  };

  post = (url, data, config = {}) => {
    const accessToken = JSON.parse(sessionStorage.getItem("authUser")).data.accessToken;
    const deviceId = getClientId();
    return axios.post(url, data, {
      headers: {
        Authorization: `Bearer ${accessToken}`,
        'Device-Id': deviceId,
        ...config.headers,
      },
      responseType: config.responseType || 'json',
    });
  };

  register = (url, data, config = {}) => {
    const deviceId = getClientId();
    return axios.post(url, data, {
      headers: {
        'Device-Id': deviceId,
        ...config.headers,
      },
      responseType: config.responseType || 'json',
    });
  };

  put = (url, data) => {
    const accessToken = JSON.parse(sessionStorage.getItem("authUser")).data.accessToken;
    const deviceId = getClientId();
    return axios.put(url, data, {
      headers: {
        Authorization: `Bearer ${accessToken}`,
        'Device-Id': deviceId,
      },
    });
  };

  delete = (url, config = {}) => {
    const accessToken = JSON.parse(sessionStorage.getItem("authUser")).data.accessToken;
    const deviceId = getClientId();
    const headers = {
      Authorization: `Bearer ${accessToken}`,
      'Device-Id': deviceId,
      ...config.headers,
    };
    return axios.delete(url, { ...config, headers });
  };
}

const getLoggedinUser = () => {
  const user = sessionStorage.getItem("authUser");
  if (!user) {
    return null;
  } else {
    return JSON.parse(user);
  }
};


export { APIClient, setAuthorization, getLoggedinUser, clearUser, setUser };

setInterval(async () => {
  const newAccessToken = await refreshAuthToken();
  if (newAccessToken) {
    setAuthorization(newAccessToken);
  }
}, 55 * 60 * 1000); 
